import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useRooms } from '@/hooks/useRooms';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { format } from 'date-fns';

interface QuickRoomAssignPanelProps {
  /** When provided, restricts the panel to these client IDs (e.g. just-imported batch). */
  clientIds?: string[];
  siteId: string;
  onDone?: () => void;
}

interface PendingClient {
  id: string;
  first_name: string | null;
  last_name: string | null;
  date_of_birth: string | null;
  phone: string | null;
}

export function QuickRoomAssignPanel({ clientIds, siteId, onDone }: QuickRoomAssignPanelProps) {
  const { rooms } = useRooms();
  const queryClient = useQueryClient();
  const [clients, setClients] = useState<PendingClient[]>([]);
  const [assignments, setAssignments] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load the target clients
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      let query = supabase
        .from('clients')
        .select('id, first_name, last_name, date_of_birth, phone')
        .eq('site_id', siteId)
        .is('room_id', null)
        .is('archived_at', null);

      if (clientIds && clientIds.length > 0) {
        query = query.in('id', clientIds);
      } else {
        query = query.eq('intake_source', 'excel_bulk');
      }

      const { data, error } = await query.order('created_at', { ascending: false });
      if (cancelled) return;
      if (error) {
        toast.error(error.message);
        setClients([]);
      } else {
        setClients(data || []);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [clientIds, siteId]);

  // Build occupancy map per room from current clients query cache (rooms.is_occupied is unreliable)
  const occupancyByRoom = useMemo(() => {
    const cached = queryClient.getQueryData<any[]>(['clients']) || [];
    const map: Record<string, number> = {};
    for (const c of cached) {
      const rid = c.room_id || c.roomId;
      if (rid && !c.archived_at) map[rid] = (map[rid] || 0) + 1;
    }
    return map;
  }, [queryClient, clients]);

  const facilityRooms = useMemo(() => {
    const used: Record<string, number> = { ...occupancyByRoom };
    // Account for in-panel pending assignments
    for (const rid of Object.values(assignments)) {
      if (rid) used[rid] = (used[rid] || 0) + 1;
    }
    return rooms
      .filter((r: any) => r.site_id === siteId)
      .map((r: any) => ({
        ...r,
        usedCount: occupancyByRoom[r.id] || 0,
        capacity: r.capacity || 1,
      }))
      .sort((a: any, b: any) => String(a.room_number).localeCompare(String(b.room_number), undefined, { numeric: true }));
  }, [rooms, siteId, occupancyByRoom, assignments]);

  const handleSaveAll = async () => {
    const updates = Object.entries(assignments).filter(([, rid]) => rid);
    if (updates.length === 0) {
      toast.info('Pick a room for at least one client first');
      return;
    }
    setSaving(true);
    let ok = 0;
    let fail = 0;
    for (const [clientId, roomId] of updates) {
      const { error } = await supabase
        .from('clients')
        .update({ room_id: roomId, status: 'checked-in', check_in_time: new Date().toISOString() })
        .eq('id', clientId);
      if (error) {
        fail++;
        toast.error(`Failed: ${error.message}`);
      } else {
        ok++;
      }
    }
    setSaving(false);
    queryClient.invalidateQueries({ queryKey: ['clients'] });
    queryClient.invalidateQueries({ queryKey: ['rooms'] });
    if (ok > 0) toast.success(`Assigned ${ok} client${ok === 1 ? '' : 's'} to rooms`);
    if (fail === 0) onDone?.();
    else {
      // refresh remaining
      setClients(prev => prev.filter(c => !updates.find(([cid]) => cid === c.id && !fail)));
      setAssignments({});
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (clients.length === 0) {
    return (
      <Alert>
        <AlertDescription>No clients are awaiting room assignment.</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      <Alert>
        <AlertDescription>
          {clients.length} client{clients.length === 1 ? '' : 's'} awaiting a bed. Pick a room for each, then save.
        </AlertDescription>
      </Alert>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left">
              <th className="px-3 py-2 font-medium">Name</th>
              <th className="px-3 py-2 font-medium">DOB</th>
              <th className="px-3 py-2 font-medium">Phone</th>
              <th className="px-3 py-2 font-medium w-56">Assign Room</th>
            </tr>
          </thead>
          <tbody>
            {clients.map(c => (
              <tr key={c.id} className="border-t">
                <td className="px-3 py-2">{c.first_name} {c.last_name}</td>
                <td className="px-3 py-2 text-muted-foreground">
                  {c.date_of_birth ? format(new Date(c.date_of_birth), 'MMM d, yyyy') : '—'}
                </td>
                <td className="px-3 py-2 text-muted-foreground">{c.phone || '—'}</td>
                <td className="px-3 py-2">
                  <Select
                    value={assignments[c.id] || ''}
                    onValueChange={(v) => setAssignments(prev => ({ ...prev, [c.id]: v }))}
                  >
                    <SelectTrigger className="h-8">
                      <SelectValue placeholder="Select room…" />
                    </SelectTrigger>
                    <SelectContent>
                      {facilityRooms.map((r: any) => {
                        const full = r.usedCount >= r.capacity;
                        return (
                          <SelectItem key={r.id} value={r.id} disabled={full}>
                            {r.room_number} ({r.usedCount}/{r.capacity}){full ? ' — full' : ''}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onDone} disabled={saving}>Done</Button>
        <Button onClick={handleSaveAll} disabled={saving}>
          {saving ? 'Saving…' : 'Save All'}
        </Button>
      </div>
    </div>
  );
}
